import React, { useCallback, useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { es } from "date-fns/locale/es";

registerLocale("es", es);

const noop = () => {};

const DatePickerRanger = ({
  startDate = null,
  endDate = null,
  onChange = noop,
  locale = "es",
  selectsRange = true,
  showTimeSelect = false,
  timeIntervals = 30,
  dateFormat,
  shouldCloseOnSelect,
  className = "form-control",
  ...restProps
}) => {
  const effectiveDateFormat = useMemo(() => {
    if (dateFormat) {
      return dateFormat;
    }
    return showTimeSelect ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy";
  }, [dateFormat, showTimeSelect]);

  const effectiveShouldCloseOnSelect = useMemo(() => {
    if (typeof shouldCloseOnSelect === "boolean") {
      return shouldCloseOnSelect;
    }
    // Mantén el popover abierto cuando se selecciona un rango o se ajustan horas
    return !(selectsRange || showTimeSelect);
  }, [selectsRange, showTimeSelect, shouldCloseOnSelect]);

  const handleChange = useCallback(
    (value) => {
      if (Array.isArray(value)) {
        const [rawStart, rawEnd] = value;
        onChange([rawStart ?? null, rawEnd ?? null]);
        return;
      }

      if (!(value instanceof Date) && value !== null) {
        return;
      }

      const candidate = value ?? null;

      if (!selectsRange) {
        onChange([candidate, candidate]);
        return;
      }

      const currentStart = startDate ?? null;
      const currentEnd = endDate ?? null;

      let nextStart = currentStart;
      let nextEnd = currentEnd;

      const candidateTime =
        candidate instanceof Date ? candidate.getTime() : null;
      const startTime =
        currentStart instanceof Date ? currentStart.getTime() : null;
      const endTime = currentEnd instanceof Date ? currentEnd.getTime() : null;

      if (currentEnd && candidateTime !== null && endTime !== null) {
        const diffToEnd = Math.abs(candidateTime - endTime);
        const diffToStart =
          startTime !== null
            ? Math.abs(candidateTime - startTime)
            : Number.POSITIVE_INFINITY;

        if (diffToEnd <= diffToStart) {
          nextEnd = candidate;
        } else {
          nextStart = candidate;
        }
      } else if (currentStart === null || candidateTime === null) {
        nextStart = candidate;
        if (
          nextEnd instanceof Date &&
          nextStart instanceof Date &&
          nextStart > nextEnd
        ) {
          nextEnd = nextStart;
        }
      } else if (startTime !== null && candidateTime < startTime) {
        nextStart = candidate;
      } else {
        nextEnd = candidate;
      }

      onChange([nextStart ?? null, nextEnd ?? null]);
    },
    [endDate, onChange, selectsRange, startDate],
  );

  return (
    <DatePicker
      locale={locale}
      selected={startDate}
      startDate={startDate}
      endDate={endDate}
      onChange={handleChange}
      selectsRange={selectsRange}
      showTimeSelect={showTimeSelect}
      timeIntervals={timeIntervals}
      dateFormat={effectiveDateFormat}
      shouldCloseOnSelect={effectiveShouldCloseOnSelect}
      className={className}
      withPortal
      showMonthDropdown
      showYearDropdown
      {...restProps}
    />
  );
};

export default DatePickerRanger;
